import {
  createEngagementSession,
  openConversation,
  type BotOption,
  type BotTurn,
  type Conversation,
} from '@pypestream/testview/browser';

import { buildEngagementApiUrl } from '../../utils/engagement-api-url';
import type {
  RunChannel,
  ValidateTurn,
  ValidateWebview,
} from './validate-runs';
import { addSubmission, type WebviewSubmission } from './webview-submission';

/**
 * Validate's Test chat: one real conversation with a channel, over the
 * Engagement API socket, the way the testing tool's `EngagementApiTestRunner`
 * drives one — but typed by a person, turn by turn.
 *
 * Built on `@pypestream/testview`'s session and conversation (the testing
 * tool's protocol, already ported into this repo): the socket is opened on
 * `wss://<host>/socket` so Phoenix appends `/websocket` once, and
 * `chat:start` / `msg:send` / `chat:end` go through the channel with the
 * access token, never the REST endpoints.
 *
 * Nothing opens until the first message. Opening Validate must not start a
 * conversation — it lands in the channel's analytics and transcripts like any
 * other.
 */

export type LiveChatStatus =
  | 'idle'
  | 'connecting'
  | 'ready'
  | 'waiting'
  | 'ended'
  | 'error';

export interface LiveChatState {
  status: LiveChatStatus;
  transcript: ValidateTurn[];
  chatId: string | null;
  error: string | null;
}

/** One reply the designer got — the unit Validate scores as a Test run. */
export interface LiveReply {
  chatId: string;
  /** The conversation so far, ending in this reply. */
  transcript: ValidateTurn[];
  focusIndex: number;
  startedAt: number;
  endedAt: number;
}

export interface LiveChatDeps {
  /**
   * Once per conversation, when it is over: the bot's flow ended it, or the
   * designer ended it, started a new chat or switched channel. Only for a
   * conversation that got as far as a customer message.
   */
  onEnded?: (ended: LiveEnded) => void;
  createSession?: typeof createEngagementSession;
  openConv?: typeof openConversation;
  origin?: string;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

/**
 * A webview in a bot turn's frames, the two ways the platform sends one (the
 * same two Pype renders): an `app_object` of type `webview` with its `url`,
 * or a `solution_request` of type `webview` (the sensitive-data form) in
 * `message_metadata`. The text harness keeps only text and buttons, so it is
 * read off the raw frames here. The last one wins, like the live prompt.
 */
export const webviewOf = (frames: unknown[]): ValidateWebview | undefined => {
  let found: ValidateWebview | undefined;
  frames.forEach((frame) => {
    if (!isRecord(frame)) return;
    const app = frame.app_object;
    if (
      isRecord(app) &&
      app.type === 'webview' &&
      typeof app.url === 'string'
    ) {
      found = {
        url: app.url,
        ...(typeof app.label === 'string' && app.label
          ? { label: app.label }
          : {}),
        ...(typeof app.close_message === 'string' && app.close_message
          ? { closeMessage: app.close_message }
          : {}),
      };
    }
    const meta = frame.message_metadata;
    const data = isRecord(meta) ? meta.data : undefined;
    if (
      isRecord(meta) &&
      meta.type === 'solution_request' &&
      isRecord(data) &&
      data.solution_type === 'webview' &&
      typeof data.webview_url === 'string' &&
      data.webview_url
    ) {
      found = {
        url: data.webview_url,
        ...(typeof data.close_message === 'string' && data.close_message
          ? { closeMessage: data.close_message }
          : {}),
      };
    }
  });
  return found;
};

/** Anything in the turn to show: text, buttons or a webview. */
const hasContent = (turn: BotTurn) =>
  !!turn.text || turn.options.length > 0 || !!webviewOf(turn.frames);

const toBotTurn = (turn: BotTurn): ValidateTurn => {
  const webview = webviewOf(turn.frames);
  return {
    role: 'bot',
    text: turn.text,
    ...(turn.options.length ? { options: turn.options } : {}),
    ...(turn.latencyMs !== undefined ? { latencyMs: turn.latencyMs } : {}),
    ...(turn.system || turn.agent ? { system: true } : {}),
    ...(turn.agent ? { agent: true } : {}),
    ...(webview ? { webview } : {}),
  };
};

/** A finished Test conversation, handed over to be scored as a whole. */
export interface LiveEnded {
  chatId: string;
  transcript: ValidateTurn[];
}

const errorText = (err: unknown) =>
  err instanceof Error ? err.message : 'Something went wrong.';

export class LiveChat {
  private conversation: Conversation | null = null;
  private opening: Promise<Conversation> | null = null;
  /** Bumped by `reset`, so work started before it can't write after it. */
  private generation = 0;
  private state: LiveChatState = {
    status: 'idle',
    transcript: [],
    chatId: null,
    error: null,
  };
  private readonly listeners = new Set<(s: LiveChatState) => void>();

  /** The chat `onEnded` last fired for, so each fires once. */
  private endedFor: string | null = null;

  constructor(
    private readonly channel: RunChannel,
    private readonly onReply: (reply: LiveReply) => void,
    private readonly deps: LiveChatDeps = {}
  ) {}

  private fireEnded() {
    const { chatId, transcript } = this.state;
    if (!chatId || this.endedFor === chatId) return;
    if (!transcript.some((t) => t.role === 'user')) return;
    this.endedFor = chatId;
    this.deps.onEnded?.({ chatId, transcript });
  }

  subscribe(fn: (s: LiveChatState) => void): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  getState(): LiveChatState {
    return this.state;
  }

  private set(patch: Partial<LiveChatState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l(this.state));
  }

  private append(turn: ValidateTurn) {
    this.set({ transcript: [...this.state.transcript, turn] });
  }

  /** Session, socket, `chat:start`, then the greeting — once per chat. */
  private open(): Promise<Conversation> {
    if (this.conversation) return Promise.resolve(this.conversation);
    if (this.opening) return this.opening;

    const { generation } = this;
    const {
      createSession = createEngagementSession,
      openConv = openConversation,
      origin = window.location.origin,
    } = this.deps;
    const engagementApiUrl = buildEngagementApiUrl(origin, this.channel.env);

    this.set({ status: 'connecting', error: null });
    this.opening = (async () => {
      const session = await createSession({
        appId: this.channel.appId,
        engagementApiUrl,
      });
      const conversation = await openConv({
        appId: this.channel.appId,
        session,
        engagementApiUrl,
      });
      if (generation !== this.generation) {
        // Reset while this was opening: the chat is real on the backend, so
        // end it there rather than only dropping the socket.
        void conversation.end().catch(() => undefined);
        throw new Error('superseded');
      }
      this.conversation = conversation;
      this.set({ chatId: conversation.chatId });

      const greeting = await conversation.greeting();
      if (generation === this.generation && greeting && hasContent(greeting)) {
        this.append(toBotTurn(greeting));
      }
      // After a hand-off a live agent answers on their own time, not in reply
      // to a send, so their messages (and "agent joined") arrive here.
      conversation.onUnprompted((turn) => {
        if (generation !== this.generation) return;
        if (!hasContent(turn)) return;
        this.append(toBotTurn(turn));
      });
      return conversation;
    })();

    return this.opening.finally(() => {
      this.opening = null;
    });
  }

  /**
   * Open the chat without saying anything, so the tester sees the greeting
   * first — the "Start chat" button. Sending a message still opens it too.
   */
  async start(): Promise<void> {
    if (this.state.status !== 'idle' && this.state.status !== 'error') return;
    const { generation } = this;
    try {
      const conversation = await this.open();
      if (generation !== this.generation) return;
      this.set({
        status: conversation.isEnded() ? 'ended' : 'ready',
        error: null,
      });
    } catch (err) {
      if (generation !== this.generation) return;
      this.set({ status: 'error', error: errorText(err) });
    }
  }

  send(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return Promise.resolve();
    return this.exchange({ role: 'user', text: trimmed }, (c) =>
      c.send(trimmed)
    );
  }

  pick(option: BotOption): Promise<void> {
    return this.exchange({ role: 'user', text: option.label }, (c) =>
      c.sendOption(option)
    );
  }

  /** Data a webview handed over, sent before anything after it. */
  private webviewData: Promise<void> = Promise.resolve();

  /** What the open webview has handed over so far, kept for its close turn. */
  private submission: WebviewSubmission | null = null;

  /**
   * A webview handed over data (`PYPESTREAM_WEBVIEW_DATA_PASSTHROUGH`): sent
   * to the bot silently, as the widget does (`metadata:send`). The bot reads
   * it with the close message that follows.
   */
  sendWebviewData(
    data: Record<string, unknown> | unknown[],
    form = 'the webview'
  ): void {
    const { conversation } = this;
    if (!conversation?.sendMetadata) return;
    this.submission = addSubmission(this.submission, form, data);
    const send = conversation.sendMetadata.bind(conversation);
    this.webviewData = this.webviewData
      .then(() => send(data))
      .catch((err: unknown) => {
        this.set({
          error: `Couldn’t pass the webview’s data on: ${errorText(err)}`,
        });
      });
  }

  /**
   * A webview closed: its close message goes as the customer's turn, after
   * any data it handed over — what the widget does (`onOverlayDismiss`).
   */
  closeWebview(message: string): Promise<void> {
    const submitted = this.submission;
    this.submission = null;
    return this.exchange(
      { role: 'user', text: message, ...(submitted ? { submitted } : {}) },
      async (c) => {
        await this.webviewData;
        return c.sendPlain ? c.sendPlain(message) : c.send(message);
      }
    );
  }

  private async exchange(
    userTurn: ValidateTurn,
    push: (c: Conversation) => Promise<BotTurn>
  ): Promise<void> {
    if (this.state.status === 'waiting' || this.state.status === 'connecting') {
      return;
    }
    if (this.state.status === 'ended') return;

    const { generation } = this;
    let conversation: Conversation;
    try {
      conversation = await this.open();
    } catch (err) {
      if (generation !== this.generation) return;
      this.set({ status: 'error', error: errorText(err) });
      return;
    }
    if (generation !== this.generation) return;

    this.append({ ...userTurn, at: Date.now() });
    this.set({ status: 'waiting', error: null });
    const startedAt = Date.now();

    try {
      const reply = await push(conversation);
      if (generation !== this.generation) return;
      // Sent to a live agent: no bot reply to show or score; the agent's
      // answer comes through `onUnprompted`.
      if (!hasContent(reply) && conversation.isWithAgent()) {
        this.set({ status: conversation.isEnded() ? 'ended' : 'ready' });
        return;
      }
      // Nothing said back (a webview that only set variables): no bubble.
      if (!hasContent(reply)) {
        this.set({ status: conversation.isEnded() ? 'ended' : 'ready' });
        return;
      }
      this.append(toBotTurn(reply));
      const over = conversation.isEnded();
      this.set({ status: over ? 'ended' : 'ready' });
      this.onReply({
        chatId: conversation.chatId,
        transcript: this.state.transcript,
        focusIndex: this.state.transcript.length - 1,
        startedAt,
        endedAt: Date.now(),
      });
      if (over) this.fireEnded();
    } catch (err) {
      if (generation !== this.generation) return;
      const message = errorText(err);
      // A bot that ended the chat can't be talked to again; a slow reply can.
      const ended = /ended/i.test(message);
      this.set({ status: ended ? 'ended' : 'ready', error: message });
      if (ended) this.fireEnded();
    }
  }

  /** End the chat on the backend. The transcript stays until `reset`. */
  async end(): Promise<void> {
    const { conversation } = this;
    this.conversation = null;
    this.set({ status: 'ended' });
    this.fireEnded();
    await conversation?.end().catch(() => undefined);
  }

  /** End any chat and start over empty; the next message opens a new one. */
  async reset(): Promise<void> {
    this.fireEnded();
    this.generation += 1;
    const { conversation } = this;
    this.conversation = null;
    this.opening = null;
    this.submission = null;
    this.set({ status: 'idle', transcript: [], chatId: null, error: null });
    await conversation?.end().catch(() => undefined);
  }

  /** Tear down without waiting — unmount, or the channel changed. */
  dispose(): void {
    this.fireEnded();
    this.generation += 1;
    this.listeners.clear();
    const { conversation } = this;
    this.conversation = null;
    void conversation?.end().catch(() => undefined);
  }
}
